import { Card } from '@app/features/analytics/components';
import { ErrorScreen } from '@app/features/analytics/components/error-states/error-screen';
import { NoFilterResults } from '@app/features/analytics/components/error-states/no-filter-results';
import { NoResults } from '@app/features/analytics/components/error-states/no-results';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFetch } from '@app/features/analytics/hooks/use-fetch';
import { useSpaceContext } from '@app/features/analytics/hooks/use-space-context';
import { FaceGrinHeartsIcon } from '@app/features/analytics/icons/face-grin-hearts';
import { FaceLaughRelaxedIcon } from '@app/features/analytics/icons/face-laugh-relaxed';
import { FaceSmileIcon } from '@app/features/analytics/icons/face-smile';
import { useAnalyticsContext } from '@app/features/analytics/providers/analytics-provider';
import { fetchRegistrants } from '@app/features/analytics/services/endpoints';
import { InterHeaderStyles } from '@app/features/analytics/styles';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect, useState } from 'react';

import { RegistrantType } from '../../registrants/types';
import LoaderSkeleton from '../loader/loader';

const ListView = ({ data }: { data: RegistrantType[] }) => {
  return <div>{data.map(attendee => EngagementRow(attendee))}</div>;
};

const getIconFromScore = (score: number) => {
  let icon;
  if (score < 50 || !score) {
    icon = <FaceSmileIcon />;
  } else if (score < 71) {
    icon = <FaceLaughRelaxedIcon />;
  } else {
    icon = <FaceGrinHeartsIcon />;
  }
  return <div>{icon}</div>;
};

const EngagementText = (name: string | number) => {
  return <span className={`block ${InterHeaderStyles} leading-6`}>{name}</span>;
};

const EngagementScore = (score: number) => {
  const scoreText = (
    <div className="font-medium text-[#8C8C8C]">
      {EngagementText(score || 0)}
    </div>
  );

  return (
    <div className="absolute right-0 top-3 w-[56px]">
      <div className="flex justify-items-start space-x-2">
        {getIconFromScore(score)}
        {scoreText}
      </div>
    </div>
  );
};

const EngagementRow = (attendee: RegistrantType) => {
  return (
    <div
      className="relative flex justify-between break-words border-t border-neutral-b4 pt-3 pb-3 leading-6 first:border-t-0"
      data-testid="listitem"
    >
      <div className="mr-2 text-sm">
        {EngagementText(`${attendee.firstName} ${attendee.lastName}`)}
      </div>
      <div className="my-auto flex-1 font-medium text-sm truncate mr-4 text-gray-400">
        {attendee.email}
      </div>
      <div className="flex gap-1 justify-end flex-grow-0 text-sm">
        <div className="min-w-12">
          {EngagementScore(attendee.engagementScore)}
        </div>
      </div>
    </div>
  );
};

export const MostEngagedAttendees = () => {
  const { state, dispatch } = useAnalyticsContext();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { track } = useAnalytics();
  const { spaceExternalId } = useSpaceContext();

  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    eventTypes: state.eventTypes,
    name: 'engagementScore',
    direction: 'desc',
    perPage: '5',
    selectedEvents: state.selectedEvents,
    spaceExternalId,
  };

  const { data, isLoading, isError } = useFetch(
    config,
    `mostEngagedAttendees-${orgId}`,
    fetchRegistrants,
  );
  const { t } = useTranslation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const result = {
    attendeesData: data?.registrants || [],
    pagination: data?.pagination || {},
  };
  const hasData = result.attendeesData.length > 0;
  const hasNextPage = result.pagination.totalCount > 5;
  if (hasData) {
    track('ola_overview_most_engaged_attendees_view_all_click', {});
  }

  const onViewAll = () => {
    dispatch({
      type: 'analytics tab updated',
      payload: { index: 3, tabName: 'registrants' },
    });
    dispatch({
      type: 'update registrants sort key and direction',
      payload: { name: 'engagementScore', direction: 'desc' },
    });
    dispatch({
      type: 'update search text',
      payload: { searchText: '' },
    });
  };

  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading]);

  return (
    <Card
      heading={t('analytics.most-engaged-attendees')}
      tooltip={t('analytics.most-engaged-attendees-tooltip')}
      subHeading={t('analytics.most-engaged-attendees-content')}
      loading={isLoading}
      showFooter={hasData && hasNextPage && !isError}
      onViewAll={onViewAll}
    >
      <>
        {isError && !isLoading && <ErrorScreen showButton={false} />}
        {!isError && !isLoading && !hasData && isInitialLoad && <NoResults />}
        {!isError && !isLoading && !hasData && !isInitialLoad && (
          <NoFilterResults />
        )}
        {!isError && !isLoading && hasData && (
          <ListView data={result.attendeesData} />
        )}
        {isLoading && <LoaderSkeleton hasData={hasData} />}
      </>
    </Card>
  );
};
